import { toaster } from 'baseui/toast';
import React from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { EzMultitext } from '../../Components/EzForm';
import { FinalButton } from '../../Components/FinalButton';
import { InputRHF, ToggleRHF } from '../../Components/Form';
import { LoadingPage } from '../../Components/LoadingOverlay';
import { Text } from '../../globalStyles';
import {
  SaiOptionsInput,
  useGetSaiOptionsQuery,
  useOrganizationEntitlementsQuery,
  useUpdateSaiOptionsMutation,
} from '../../graphQL';
import { UnexpectedError } from '../Shared';
import { SurveyPeriod } from './SurveyPeriod';
import { useOrganizationId } from './util';

type SaiOptionsFormType = {
  isSurveySegmented: boolean;
  surveySegmentationMappingValues: string[];
  surveySegmentationKey?: string;
};

const defaultValues: SaiOptionsFormType = {
  isSurveySegmented: false,
  surveySegmentationMappingValues: [],
  surveySegmentationKey: '',
};

export function OrganizationSAIOptions(): JSX.Element {
  const organizationId = useOrganizationId();
  const form = useForm<SaiOptionsFormType>({
    defaultValues,
  });

  // useWatch tracks changes more safely than form.watch(), which caused issues with re-renders and dirty state
  const isSurveySegmented = useWatch({
    control: form.control,
    name: 'isSurveySegmented',
  });

  const {
    data: saiData,
    loading: saiLoading,
    error: saiError,
    refetch,
  } = useGetSaiOptionsQuery({
    variables: { orgId: organizationId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: result => {
      const { getSaiOptions } = result;
      if (getSaiOptions) {
        form.reset({
          isSurveySegmented: getSaiOptions.isSurveySegmented,
          surveySegmentationKey: getSaiOptions.surveySegmentationKey || '',
          surveySegmentationMappingValues: getSaiOptions.surveySegmentationMappingValues || [],
        });
      }
    },
  });

  const { data: orgData } = useOrganizationEntitlementsQuery({
    variables: { id: organizationId },
  });

  const [updateSaiOptions, mutation] = useUpdateSaiOptionsMutation();

  if ((!saiData && saiLoading) || !orgData) return <LoadingPage />;
  if (!saiData || saiError) return <UnexpectedError />;

  const handleSave = form.handleSubmit(async values => {
    const update: SaiOptionsInput = {
      organizationId,
      isSurveySegmented: values.isSurveySegmented,
      surveySegmentationMappingValues: values.surveySegmentationMappingValues,
      surveySegmentationKey: values.surveySegmentationKey,
    };
    await updateSaiOptions({
      variables: {
        data: update,
      },
    });
    refetch();
    toaster.positive('SAI options saved successfully', { autoHideDuration: 3000 });
  });

  return (
    <div style={{ maxWidth: 800 }} className="mb4">
      <Text.h2>SAI Options</Text.h2>
      <div className="mt4">
        <Text.h3>Survey Options</Text.h3>
        <FormProvider {...form}>
          <form onSubmit={handleSave}>
            <div className="mt4">
              <ToggleRHF name="isSurveySegmented">
                <label className="db">
                  <Text.bodyBold>Enable Survey Segmentation</Text.bodyBold>
                  <Text.bodySmall>
                    When enabled, only students with matching segmentation values will have access
                    to the survey. When disabled, all students in this organization will have access
                    to the survey.
                  </Text.bodySmall>
                </label>
              </ToggleRHF>
            </div>

            <div style={{ display: isSurveySegmented ? 'block' : 'none' }}>
              <div className="mt4">
                <InputRHF
                  name="surveySegmentationKey"
                  placeholder="Survey Segmentation Key"
                  controlProps={{
                    label: (
                      <label className="db mb2">
                        <Text.bodyBold>Survey Segmentation Key</Text.bodyBold>
                        <Text.bodySmall>
                          The custom demographic field key that will be used to match against the
                          eligibility values below.
                        </Text.bodySmall>
                      </label>
                    ),
                  }}
                />
              </div>
              <div className="mt4">
                <label className="db mb2">
                  <Text.bodyBold>Student Eligibility Values (Partner-Provided)</Text.bodyBold>
                  <Text.bodySmall>
                    Values that will be matched against the student's custom demographic data for
                    the segmentation key above. Each value should correlate with a partner-provided
                    value.
                  </Text.bodySmall>
                </label>
                <Controller
                  name="surveySegmentationMappingValues"
                  control={form.control}
                  render={({ value, onChange }) => (
                    <EzMultitext
                      placeholder={{ numbered: true, text: 'Custom Value From Partner' }}
                      value={value}
                      onChange={c => onChange(c)}
                    />
                  )}
                />
              </div>
            </div>
            <div className="mt4">
              <FinalButton
                kind="primary"
                loading={mutation.loading}
                disabled={!form.formState.isDirty}
                onClick={handleSave}
              >
                Save SAI Options
              </FinalButton>
            </div>

            {mutation.error && (
              <div className="mt3">
                <Text.bodySmall kind="danger">
                  Error saving options: {mutation.error.message}
                </Text.bodySmall>
              </div>
            )}
          </form>
        </FormProvider>
      </div>

      {/* Survey Period Config Section */}
      <SurveyPeriod organizationId={organizationId} organizationName={orgData.organization.name} />
    </div>
  );
}
